// Hook: Fetches a single workflow instance with full task steps and subtasks
// Run `yarn codegen` after adding queries.federation.gql to generate typed hooks.

import { useMemo } from "react";
import { useGetWorkflowQuery } from "#graphql/supergraphHooks";
import { TaskType as TaskTypeEnum, SubtaskItemType } from "../types";
import type { GetWorkflowQuery } from "#graphql/supergraphTypes";
import type {
  WorkflowInstanceRow,
  TaskStepSummary,
  TaskStepDetail,
  SubtaskItem,
  ActivityEvent,
  ActionConfig,
} from "../types";

type WorkflowGql = NonNullable<GetWorkflowQuery["workflow"]>;
type TaskGql = WorkflowGql["tasks"][0];
type SubtaskGql = TaskGql["subtasks"][0];
type ActivityEventGql = TaskGql["activityLog"][0];

// Child subtask IDs are minted as "<parentId>:<trigger>:<index>" so the trailing
// integer is the authoritative display order. BE re-serializes conditionalChildren
// on every update and the JSON pass does not always preserve insertion order, so
// sort defensively here.
function trailingChildIndex(id: number | string): number {
  if (typeof id === "number") return id;
  const match = /:(\d+)$/.exec(id);
  return match ? Number(match[1]) : 0;
}

function mapConditionalChildren(
  conditionalChildren: Record<string, SubtaskGql[]>,
): Record<string, SubtaskItem[]> {
  const out: Record<string, SubtaskItem[]> = {};
  for (const [key, children] of Object.entries(conditionalChildren)) {
    out[key] = [...children]
      .map(mapSubtask)
      .sort((a, b) => trailingChildIndex(a.id) - trailingChildIndex(b.id));
  }
  return out;
}

function mapActivityEvent(e: ActivityEventGql): ActivityEvent {
  return {
    id: e.id,
    occurredAt: e.occurredAt,
    actorName: e.actorName ?? null,
    actorId: e.actorId ?? null,
    action: e.action,
    note: e.note ?? null,
    metaData: e.metaData,
    taskId: e.taskId ?? null,
    workflowId: e.workflowId ?? null,
  };
}

function mapSubtask(s: SubtaskGql): SubtaskItem {
  return {
    id: s.id,
    itemType: (s.itemType as SubtaskItemType) ?? SubtaskItemType.CHECKBOX,
    label: s.label,
    helpText: s.helpText ?? undefined,
    isCompleted: s.isCompleted,
    isRequired: s.isRequired ?? undefined,
    value: s.value ?? undefined,
    options: s.options != null ? (s.options as string[]) : undefined,
    conditional_children:
      s.conditionalChildren != null
        ? mapConditionalChildren(s.conditionalChildren as Record<string, SubtaskGql[]>)
        : undefined,
    config: s.config != null ? (s.config as Record<string, unknown>) : undefined,
    dataType: (s.dataType as string | null | undefined) ?? undefined,
    uiSchema: s.uiSchema != null ? (s.uiSchema as Record<string, unknown>) : undefined,
    isCustom: (s.isCustom as boolean | null | undefined) ?? undefined,
  };
}

// BE returns every not-yet-active task as NOT_STARTED, which the UI shows as
// "Blocked". Surface workflow.currentStepName as the in-progress step so users
// can see which task is actionable.
function effectiveStatus(rawStatus: string, taskName: string, currentStepName: string | null) {
  if (rawStatus === "NOT_STARTED" && currentStepName && taskName === currentStepName) {
    return "IN_PROGRESS";
  }
  return rawStatus;
}

function mapTaskSummary(
  task: TaskGql,
  index: number,
  currentStepName: string | null,
): TaskStepSummary {
  return {
    id: task.id,
    stepOrder: task.stepOrder ?? index,
    name: task.name,
    status: effectiveStatus(task.status, task.name, currentStepName),
    gpxStatus: task.gpxStatus,
    executionState: task.executionState,
    taskType: (task.taskType as TaskTypeEnum) ?? null,
    responsibleParty: task.responsibleParty ?? null,
    assignmentValue: task.assignmentValue ?? null,
    assignee: task.assignee
      ? { id: task.assignee.id, displayName: task.assignee.displayName }
      : null,
    dueDate: task.dueDate ?? null,
  };
}

function getMirroredSubtasks(task: TaskGql): SubtaskGql[] {
  const raw = (task as unknown as Record<string, unknown>)["mirroredSubtasks"];
  if (!Array.isArray(raw)) return [];
  return raw as SubtaskGql[];
}

function mapTaskDetail(
  task: TaskGql,
  index: number,
  currentStepName: string | null,
): TaskStepDetail {
  return {
    ...mapTaskSummary(task, index, currentStepName),
    nodeKind: task.nodeKind ?? undefined,
    nodeKey: task.nodeKey ?? undefined,
    requiresGpApproval: task.requiresGpApproval ?? false,
    assignee: task.assignee
      ? { id: task.assignee.id, displayName: task.assignee.displayName }
      : null,
    maker: task.maker ? { id: task.maker.id, displayName: task.maker.displayName } : null,
    checker: task.checker
      ? { id: task.checker.id, displayName: task.checker.displayName }
      : null,
    availableActions: task.availableActions.map(
      (a): ActionConfig => ({
        action: a.action,
        label: a.label,
        variant: a.variant as ActionConfig["variant"],
        requiresConfirmation: a.requiresConfirmation,
      }),
    ),
    subtasks: [...task.subtasks].sort((a, b) => Number(a.id) - Number(b.id)).map(mapSubtask),
    mirroredSubtasks: [...getMirroredSubtasks(task)]
      .sort((a, b) => Number(a.id) - Number(b.id))
      .map(mapSubtask),
    activityLog: [...(task.activityLog ?? [])]
      .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())
      .map(mapActivityEvent),
  };
}

function mapWorkflow(wf: WorkflowGql): WorkflowInstanceRow {
  return {
    id: wf.id,
    // Detail is fetched by the engine workflow id, so here id IS the source id.
    sourceId: wf.id,
    title: wf.title,
    templateName: wf.templateName ?? null,
    // Definition scope/version drive the list-view tag only; the detail query
    // doesn't fetch them, so the detail row carries nulls.
    definitionScope: null,
    definitionVersionNumber: null,
    workflowType: wf.workflowType,
    status: wf.status,
    currentStepName: wf.currentStepName ?? null,
    currentAssignee: wf.currentAssignee
      ? { id: wf.currentAssignee.id, displayName: wf.currentAssignee.displayName }
      : null,
    clientId: wf.clientId ?? null,
    clientName: wf.clientName ?? null,
    entityId: wf.entityId ?? null,
    entityName: wf.entityName ?? null,
    client: wf.client ? { id: wf.client.id, name: wf.client.name } : null,
    entity: wf.entity ? { id: wf.entity.id, name: wf.entity.name } : null,
    createdAt: wf.createdAt ?? null,
    updatedAt: wf.updatedAt ?? null,
    dueDate: wf.dueDate ?? null,
    slaStatus: wf.slaStatus as WorkflowInstanceRow["slaStatus"],
    source: wf.source ?? null,
    externalRefId: wf.externalRefId ?? null,
    faPreparer: wf.faPreparer ?? null,
    faReviewer: wf.faReviewer ?? null,
    isPreparer: wf.isPreparer ?? null,
    isReviewer: wf.isReviewer ?? null,
    requestedBy: wf.requestedBy ?? null,
    statusCategory: wf.statusCategory ?? null,
    progress: {
      completed: wf.progress.completed,
      total: wf.progress.total,
      percentage: wf.progress.percentage,
    },
    taskSteps: wf.tasks.map((t, i) => mapTaskSummary(t, i, wf.currentStepName ?? null)),
  };
}

interface UseWorkflowDetailResult {
  workflow: WorkflowInstanceRow | null;
  taskSteps: TaskStepDetail[];
  activityLog: ActivityEvent[];
  /** Logged-in user's workflow_user.id, resolved by this query and refreshed on
   *  every refetch — same id-space as task.assignee.id. */
  viewerWorkflowUserId: string | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useWorkflowDetail(
  workflowId: string,
  /** Pass the already-loaded WorkflowInstanceRow so Jira-sourced workflows
   *  don't make a redundant GetWorkflow call (the workflow table has no row for them). */
  preloadedWorkflow?: WorkflowInstanceRow | null,
): UseWorkflowDetailResult {
  const parsedId = Number(workflowId);
  const isJira = preloadedWorkflow?.source === "JIRA";
  const skip = !workflowId || Number.isNaN(parsedId) || isJira;

  const {
    data,
    loading,
    error,
    refetch: doRefetch,
  } = useGetWorkflowQuery({
    variables: { id: skip ? 0 : parsedId },
    skip,
    fetchPolicy: "cache-and-network",
  });

  const result = useMemo(() => {
    if (!data?.workflow) {
      return { workflow: null, taskSteps: [] };
    }
    return {
      workflow: mapWorkflow(data.workflow),
      taskSteps: data.workflow.tasks.map((t, i) =>
        mapTaskDetail(t, i, data.workflow?.currentStepName ?? null),
      ),
    };
  }, [data]);

  return {
    ...result,
    activityLog: [] as ActivityEvent[],
    viewerWorkflowUserId: data?.workflow?.viewerWorkflowUserId ?? null,
    loading,
    error: error ?? null,
    refetch: () => {
      if (skip) return;
      void doRefetch();
    },
  };
}
